import * as React from 'react';

import { enumPurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import { Row } from '@rematter/ui-web';
import { formatMoney } from '@rematter/ui-web/lib';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';

import { usePurchaseLoadChangesSubscription } from '../../../_graphql/subscriptionPurchaseLoadChanges';
import { useTranslations } from '../../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../../routes';
import { useInboundPrintDocuments } from '../hooks/useInboundPrintDocuments';
import { useTicketActions } from '../hooks/useTicketActions';
import { useTicketKeyboardShortcuts } from '../hooks/useTicketKeyboardShortcuts';
import { useUStudioTicketController } from '../hooks/useUStudioTicketController';
import { UStudioScaleTicketController } from '../shared/UStudioScaleTicketController';

import { InboundTicketHeader } from './InboundTicketHeader';
import { InboundTicketTabs } from './InboundTicketTabs';

import { $purchaseTotal } from '~/modules/trade/_components/_shared/ItemsInterface/_state';
import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import { $cleanupMaterials } from '~/modules/trade/_state/materialsForm.state';
import { $headerRect } from '~/modules/trade/_state/materialsInterface.state';
import {
  $cleanupTicketDetailsForm,
  $flushTicketDetailsAutoSave,
  $syncTicketDetailsForm,
} from '~/modules/trade/_state/scaleTicketInboundInfoForm.state';
import {
  $saveInboundMaterials,
  $syncMaterialsForm,
} from '~/modules/trade/_state/scaleTicketInboundMaterialsForm.state';
import {
  $cleanupTicketNotesForm,
  $flushTicketNotesAutoSave,
  $syncTicketNotesForm,
} from '~/modules/trade/_state/scaleTicketInboundNotesForm.state';
import {
  $scaleTicketDetail,
  $scaleTicketId,
  $scaleTicketTab,
} from '~/modules/trade/_state/scaleTicketPro.state';
import {
  $cleanupTicketSupplierForm,
  $flushTicketSupplierAutoSave,
  $syncTicketSupplierForm,
} from '~/modules/trade/_state/scaleTicketSupplierForm.state';
import { type FocusPosition } from '~/shared/components/FieldFocusIndicator';
import { useHeaderRectMeasurement } from '~/shared/hooks/useHeaderRectMeasurement';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import { usePresence } from '~/shared/providers/presence';

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET DETAIL ⚛️ ⚛️ ⚛️
//
export const InboundTicketDetail = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Refs
  const prevTicketIdRef = React.useRef<string | undefined>(undefined);

  // Atoms
  const ticketId = useAtomValue($scaleTicketId);
  const { data: ticket, loadingState } = useAtomValue($scaleTicketDetail);
  const purchaseTotal = useAtomValue($purchaseTotal);
  const setTab = useSetAtom($scaleTicketTab);
  const setHeaderRect = useSetAtom($headerRect);
  const syncDetailsForm = useSetAtom($syncTicketDetailsForm);
  const syncNotesForm = useSetAtom($syncTicketNotesForm);
  const syncSupplierForm = useSetAtom($syncTicketSupplierForm);
  const syncMaterialsForm = useSetAtom($syncMaterialsForm);
  const flushDetailsAutoSave = useSetAtom($flushTicketDetailsAutoSave);
  const flushNotesAutoSave = useSetAtom($flushTicketNotesAutoSave);
  const flushSupplierAutoSave = useSetAtom($flushTicketSupplierAutoSave);
  const saveInboundMaterials = useSetAtom($saveInboundMaterials);
  const cleanupDetailsForm = useSetAtom($cleanupTicketDetailsForm);
  const cleanupNotesForm = useSetAtom($cleanupTicketNotesForm);
  const cleanupSupplierForm = useSetAtom($cleanupTicketSupplierForm);
  const cleanupInboundMaterials = useSetAtom($cleanupMaterials);
  const focusedField = useAtomValue($focusedField);
  const setRemotePositions = useSetAtom($remoteFocusPositions);

  const presenceRoom = ticketId ? `ticket-fields:${ticketId}` : '';
  const {
    members: presenceMembers,
    announce: presenceAnnounce,
    isSelf: presenceIsSelf,
  } = usePresence(presenceRoom, { join: !!ticketId });

  const presenceAnnounceRef = React.useRef(presenceAnnounce);
  presenceAnnounceRef.current = presenceAnnounce;

  const inboundTicketSubscriptionIds = React.useMemo(
    () => (ticketId && ticketId !== 'new' ? [ticketId] : []),
    [ticketId],
  );

  // Other data hooks
  const headerRef = useHeaderRectMeasurement(setHeaderRect);
  const {
    isProcessing,
    handleHold,
    handleRelease,
    handleCompleteInventory,
    handlePay,
    handleVoid,
  } = useTicketActions({ ticketId });

  const ustudio = useUStudioTicketController({
    ticketId: ticketId ?? null,
    currencyCode: ticket?.currencyCode ?? 'USD',
    saveMaterials: saveInboundMaterials,
    failedToSaveMessage: t.scaleTicketPro.materialsTab.failedToSaveMaterials,
  });

  const lastKnownVersionRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (ticket?.version != null) {
      lastKnownVersionRef.current = ticket.version;
    }
  }, [ticket?.version]);

  usePurchaseLoadChangesSubscription(
    { ids: inboundTicketSubscriptionIds },
    {
      onEvent: (signal) => {
        if (!signal || signal.version <= (lastKnownVersionRef.current ?? 0))
          return;
        getDefaultStore().get($scaleTicketDetail).refetch();
      },
    },
  );

  // Memoized values
  const status = ticket?.status;
  const ticketData = ticket || null;
  const isLoading = !!loadingState?.isInitialLoading;
  const grandTotal =
    purchaseTotal.amount !== 0
      ? formatMoney(purchaseTotal.amount, purchaseTotal.currencyCode)
      : undefined;
  const printDocuments = useInboundPrintDocuments(ticketData);

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(tradeRoutes.scaleTickets);
  }, [navigate]);

  const handleUStudioFinish = React.useCallback(() => {
    if (!status) return;
    if (
      status === enumPurchaseLoadStatus.Open ||
      status === enumPurchaseLoadStatus.Held
    ) {
      handleCompleteInventory();
    } else if (status === enumPurchaseLoadStatus.PriceComplete) {
      handlePay();
    }
  }, [status, handleCompleteInventory, handlePay]);

  // Effects
  React.useEffect(() => {
    presenceAnnounceRef.current({ focusedField });
  }, [focusedField]);

  React.useEffect(() => {
    const positions: FocusPosition[] = [];
    for (const [id, member] of presenceMembers) {
      if (presenceIsSelf(id)) continue;
      const meta = member.metadata as Record<string, unknown> | null;
      const field = meta?.focusedField;
      if (typeof field === 'string') {
        positions.push({
          userId: id,
          fieldId: field,
          name: member.name ?? undefined,
        });
      }
    }
    setRemotePositions(positions);
  }, [presenceMembers, presenceIsSelf, setRemotePositions]);

  useTicketKeyboardShortcuts({
    onBack: handleBack,
    onSetTab: setTab,
    onCompleteInventory: handleCompleteInventory,
    enabled: Boolean(ticket),
  });

  /** Flush pending auto-saves and cleanup state when navigating away from a ticket */
  React.useEffect(() => {
    const prevId = prevTicketIdRef.current;
    prevTicketIdRef.current = ticketId ?? undefined;
    if (prevId && prevId !== ticketId) {
      flushDetailsAutoSave(prevId);
      flushNotesAutoSave(prevId);
      flushSupplierAutoSave(prevId);
      saveInboundMaterials({
        ticketId: prevId,
        immediate: true,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
      cleanupDetailsForm(prevId);
      cleanupNotesForm(prevId);
      cleanupSupplierForm(prevId);
      cleanupInboundMaterials(prevId);
    }
  }, [
    ticketId,
    flushDetailsAutoSave,
    flushNotesAutoSave,
    flushSupplierAutoSave,
    saveInboundMaterials,
    cleanupDetailsForm,
    cleanupNotesForm,
    cleanupSupplierForm,
    cleanupInboundMaterials,
    t,
  ]);

  /** Sync all form state when ticket data changes */
  React.useLayoutEffect(() => {
    if (!ticketData) return;
    syncDetailsForm({
      ticketId: ticketData.id,
      ticket: ticketData,
      serverUpdatedAt: ticketData.timestamps?.updatedAt?.toString(),
    });
    syncNotesForm({
      ticketId: ticketData.id,
      ticket: ticketData,
      serverUpdatedAt: ticketData.timestamps?.updatedAt?.toString(),
    });
    syncSupplierForm({
      ticketId: ticketData.id,
      ticket: ticketData,
      serverUpdatedAt: ticketData.timestamps?.updatedAt?.toString(),
    });
    syncMaterialsForm({
      ticketId: ticketData.id,
      documentNumber: ticketData.documentNumber,
      lineItems: ticketData.lineItems ?? [],
      deductions: ticketData.deductions ?? [],
      freight: {
        freightCost: ticketData.freightCost ?? null,
        freightTerms: ticketData.freightTerms ?? null,
      },
      serverUpdatedAt: ticketData.timestamps?.updatedAt?.toString(),
    });
  }, [
    ticketData,
    syncDetailsForm,
    syncNotesForm,
    syncSupplierForm,
    syncMaterialsForm,
  ]);

  if (!ticketId) {
    return (
      <Row xCenter yCenter className="h-full w-full">
        <div className="text-neutral/50">
          {t.scaleTicketPro.detail.noTicketSelected}
        </div>
      </Row>
    );
  }

  if (!ticketData && !isLoading) {
    return (
      <Row xCenter yCenter className="h-full w-full">
        <div className="text-failure">
          {t.scaleTicketPro.detail.ticketNotFound}
        </div>
      </Row>
    );
  }

  if (!ticketData) {
    return (
      <Row xCenter yCenter className="h-full w-full">
        <div className="text-foreground/50">
          {t.scaleTicketPro.detail.noTicketSelected}
        </div>
      </Row>
    );
  }

  return (
    <div className="@InboundTicketDetail flex h-full w-full min-w-0 flex-col gap-2">
      <UStudioScaleTicketController
        {...ustudio}
        onHold={handleHold}
        onRelease={handleRelease}
        onFinish={handleUStudioFinish}
        ticketStatus={ticketData.status}
        isHeld={ticketData.status === enumPurchaseLoadStatus.Held}
      />
      <div className="flex min-h-0 flex-1 flex-col" ref={headerRef}>
        <InboundTicketTabs
          ticket={ticketData}
          isLoading={isLoading}
          header={
            <InboundTicketHeader
              ticket={ticketData}
              status={status}
              materialCount={ticket?.lineItems?.length}
              isProcessing={isProcessing}
              grandTotal={grandTotal}
              facilityId={ticket?.facilityId}
              purchaseLoadId={ticket?.id}
              existingPayments={ticket?.payments}
              printDocuments={printDocuments}
              ticketReceivedDate={ticket?.receivedDate?.toString()}
              onHold={handleHold}
              onVoid={handleVoid}
              onRelease={handleRelease}
              onPay={handlePay}
              onGoBack={handleBack}
            />
          }
        />
      </div>
    </div>
  );
};
